// --- START OF FILE Equipos.js ---

import React, { useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock, Clock, Search, ArrowUpDown, List, PieChart, Network, ArrowLeft, ChevronRight, Copy, Award } from 'lucide-react';
import Pagination from '../../components/common/Pagination';
import BotonCompartir from '../../components/BotonCompartir';
import '../../styles/Reportes.css'; // Reutilizamos los estilos de los reportes
import TransactionHistory from '../../components/common/TransactionHistory.js';
import NetworkCascadeView from '../../components/NetworkCascadeView';
import { RANK_LIST } from '../../constants/careerConfig';

const TeamMemberTable = ({ members, sortConfig, requestSort }) => {
    if (!members || members.length === 0) {
        return <p className="empty-state-text">No hay miembros en este nivel.</p>;
    }

    const SortableHeader = ({ children, sortKey }) => { // eslint-disable-line
        const isSorted = sortConfig && sortConfig.key === sortKey;
        const icon = isSorted ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : <ArrowUpDown size={14} style={{ marginLeft: '4px', opacity: 0.5 }} />;

        return (
            <th onClick={() => requestSort(sortKey)} className="sortable-header">
                {children}
                {icon}
            </th>
        );
    };

    return (
        <div className="report-table-container">
            <table className="report-table">
                <thead>
                    <tr>
                        <SortableHeader sortKey="fullName">Nombre</SortableHeader>
                        <th>Email</th>
                        <th>Acuerdos Activos</th>
                        <SortableHeader sortKey="totalCommissionsGenerated">Comisiones Generadas</SortableHeader>
                        <th>País</th>
                        <SortableHeader sortKey="createdAt">Fecha de Registro</SortableHeader>
                    </tr>
                </thead>
                <tbody>
                    {members.map(member => (
                        member && <tr key={member.id}>
                            <td data-label="Nombre">{member.fullName || 'Nombre no disponible'}</td>
                            <td data-label="Email">{member.userEmail || 'Email no disponible'}</td>
                            <td data-label="Acuerdos Activos" style={{ textAlign: 'center' }}>{member.activeAgreementsCount ?? 0}</td>
                            <td data-label="Comisiones Generadas">${(member.totalCommissionsGenerated ?? 0).toFixed(2)}</td>
                            <td data-label="País">{member.country || 'N/A'}</td>
                            <td data-label="Fecha de Registro">{member.createdAt ? new Date(member.createdAt).toLocaleDateString('es-ES') : 'N/A'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Detalle completo de "Red Profunda" (grilla de montos, barra de progreso, aviso de bloqueo e
// info del ciclo) — vive directo dentro de la tarjeta del resumen, sin sub-pestaña ni panel
// aparte, a pedido explícito de Nicolás ("quiero que los detalles estén en esa barra").
const ResidualCommissionDetail = ({ commissionCycle }) => {
    const residualUnlockPercentage = commissionCycle && commissionCycle.potentialCommission > 0
        ? Math.min(100, (commissionCycle.unlockedCommission / commissionCycle.potentialCommission) * 100)
        : 0;

    return (
        <>
            {commissionCycle && commissionCycle.lockedCommission > 0 && (
                <div className="blocked-message">
                    <Lock size={18} />
                    Tenés ${commissionCycle.lockedCommission.toFixed(2)} de comisión bloqueados
                    todavía. Generá más volumen en tus líneas 1, 2 y 3 para desbloquearlos antes
                    de que cierre el ciclo — lo que no se desbloquee se pierde al reiniciar.
                </div>
            )}

            <div className="residual-stats-grid">
                <div className="residual-stat">
                    <span className="residual-stat-label">Volumen entrante (línea 4+)</span>
                    <span className="residual-stat-value">${(commissionCycle?.line4PlusVolume || 0).toFixed(2)}</span>
                </div>
                <div className="residual-stat">
                    <span className="residual-stat-label">Comisión potencial (1%)</span>
                    <span className="residual-stat-value">${(commissionCycle?.potentialCommission || 0).toFixed(2)}</span>
                </div>
                <div className="residual-stat residual-stat-highlight">
                    <span className="residual-stat-label">Ya cobrado este ciclo</span>
                    <span className="residual-stat-value">${(commissionCycle?.paidCommission || 0).toFixed(2)}</span>
                </div>
                <div className="residual-stat">
                    <span className="residual-stat-label">Pendiente por cobrar ahora</span>
                    <span className="residual-stat-value">${(commissionCycle?.pendingCommission || 0).toFixed(2)}</span>
                </div>
            </div>

            <div className="progress-bar-container" style={{ marginTop: '1rem' }}>
                <div className="progress-bar" style={{ width: `${residualUnlockPercentage}%` }}></div>
            </div>

            <div className="cycle-info">
                <Clock size={16} />
                <span>
                    Ciclo de 30 días propio: se reinicia el{' '}
                    <strong>{commissionCycle?.cycleEnd ? new Date(commissionCycle.cycleEnd).toLocaleDateString('es-AR') : '—'}</strong>.
                    Volumen combinado de tus líneas 1-2-3 en este ciclo:{' '}
                    <strong>${(commissionCycle?.line123Volume || 0).toFixed(2)}</strong>.
                </span>
            </div>
        </>
    );
};

const EquiposDeTrabajo = ({ profileData, teamData, transactions, loading, error }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

    // Sub-vista derivada de la URL (/dashboard/teams, /dashboard/teams/red, .../lista, .../comisiones).
    // No requiere tocar el router del Dashboard: activeSection sigue siendo "teams" en los tres
    // casos, así que el sidebar/header no se remontan al navegar entre estas subpáginas.
    const pathParts = location.pathname.split('/').filter(Boolean);
    const subView = pathParts[2] || 'resumen'; // 'resumen' | 'red' | 'lista' | 'comisiones'
    const goTo = (sub) => navigate(sub ? `/dashboard/teams/${sub}` : '/dashboard/teams');

    const userReferralCode = profileData?.username;
    const { stats, allUsersByLevel, referrerUsername, commissionCycle } = teamData || {};

    const currentLevelMembers = useMemo(() => {
        if (!allUsersByLevel) return [];
        return allUsersByLevel[activeTab] || [];
    }, [allUsersByLevel, activeTab]);

    const filteredMembers = useMemo(() => {
        let sortableMembers = [...currentLevelMembers];

        if (sortConfig.key) {
            sortableMembers.sort((a, b) => {
                if (['activeAgreementsCount', 'totalCommissionsGenerated'].includes(sortConfig.key)) {
                    return sortConfig.direction === 'asc' ? (a[sortConfig.key] ?? 0) - (b[sortConfig.key] ?? 0) : (b[sortConfig.key] ?? 0) - (a[sortConfig.key] ?? 0);
                }
                if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
                if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        if (!searchTerm.trim()) {
            return sortableMembers;
        }
        return sortableMembers.filter(member =>
            (member.fullName && member.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (member.userEmail && member.userEmail.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [currentLevelMembers, searchTerm, sortConfig]);

    const requestSort = useCallback((key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    }, [sortConfig]);

    const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleTabClick = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        setSearchTerm('');
    };

    const handleCopy = async (text) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            // Silencioso: si el navegador bloquea el acceso al portapapeles, no hay mucho más
            // que hacer acá — el usuario igual puede seleccionar el texto a mano.
        }
    };

    if (loading) {
        return <div className="main-panel"><p>Cargando datos del equipo...</p></div>;
    }

    if (error || !teamData) {
        return <div className="main-panel message-box error">{error || 'No se pudieron cargar los datos del equipo.'}</div>;
    }

    const totalMembers = (stats?.[1]?.members || 0) + (stats?.[2]?.members || 0) + (stats?.[3]?.members || 0);
    const totalVolume = (stats?.[1]?.volume || 0) + (stats?.[2]?.volume || 0) + (stats?.[3]?.volume || 0);
    const totalEarnings = (stats?.[1]?.earnings || 0) + (stats?.[2]?.earnings || 0) + (stats?.[3]?.earnings || 0);
    const levelDistribution = [1, 2, 3].map(level => ({ level, members: stats?.[level]?.members || 0 }));

    // Rango del Plan de Carrera — ojo, es un sistema de puntos DISTINTO al de "Puntos" que se
    // muestra al lado (ese es el nuevo, por comisiones de red; el rango se calcula sobre
    // inversión personal, careerPath.accumulatedPoints). Son dos contadores separados a
    // propósito, no es un error que no "sumen" entre sí.
    const currentRank = RANK_LIST.find(r => r.id === (profileData?.careerPath?.currentRankId || 0)) || null;

    const residualUnlockPercentage = commissionCycle && commissionCycle.potentialCommission > 0
        ? Math.min(100, (commissionCycle.unlockedCommission / commissionCycle.potentialCommission) * 100)
        : 0;

    const paginatedMembers = filteredMembers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const referralLink = profileData?.username
        ? `${window.location.origin}/register?ref=${encodeURIComponent(profileData.username)}`
        : '';
    const referralLinkDisplay = referralLink.replace(/^https?:\/\//, '');

    // --- Subpágina: Explorar Red ---
    if (subView === 'red') {
        return (
            <div className="main-panel">
                <button type="button" className="team-back-btn" onClick={() => goTo(null)}>
                    <ArrowLeft size={18} /> Volver al resumen
                </button>
                <h2 className="section-title">Explorar Red</h2>
                <NetworkCascadeView />
            </div>
        );
    }

    // --- Subpágina: Ver Lista ---
    if (subView === 'lista') {
        return (
            <div className="main-panel">
                <button type="button" className="team-back-btn" onClick={() => goTo(null)}>
                    <ArrowLeft size={18} /> Volver al resumen
                </button>
                <h2 className="section-title">Miembros de tu Equipo</h2>

                <div className="tabs-container">
                    <button onClick={() => handleTabClick(1)} className={`tab-button ${activeTab === 1 ? 'active' : ''}`}>Nivel 1 ({stats?.[1]?.members || 0})</button>
                    <button onClick={() => handleTabClick(2)} className={`tab-button ${activeTab === 2 ? 'active' : ''}`}>Nivel 2 ({stats?.[2]?.members || 0})</button>
                    <button onClick={() => handleTabClick(3)} className={`tab-button ${activeTab === 3 ? 'active' : ''}`}>Nivel 3 ({stats?.[3]?.members || 0})</button>
                </div>

                <div className="team-level-content">
                    <div className="report-filters" style={{ marginBottom: '1.5rem', justifyContent: 'space-between' }}>
                        <div className="filter-item">
                            <Search size={16} />
                            <input type="text" placeholder="Buscar por nombre o email..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                        </div>
                    </div>
                    <TeamMemberTable members={paginatedMembers} sortConfig={sortConfig} requestSort={requestSort} />
                </div>
                <Pagination totalItems={filteredMembers.length} itemsPerPage={itemsPerPage} onItemsPerPageChange={setItemsPerPage} currentPage={currentPage} onNext={handleNextPage} onPrevious={handlePreviousPage} canNext={currentPage < totalPages} canPrevious={currentPage > 1} />
            </div>
        );
    }

    // --- Subpágina: Comisiones ---
    if (subView === 'comisiones') {
        return (
            <div className="main-panel">
                <button type="button" className="team-back-btn" onClick={() => goTo(null)}>
                    <ArrowLeft size={18} /> Volver al resumen
                </button>
                <h2 className="section-title">Reporte de Comisiones</h2>
                <TransactionHistory transactions={transactions} showTabs={false} defaultTab="Comisiones" />
            </div>
        );
    }

    // --- Resumen (pantalla principal) ---
    return (
        <div className="main-panel">
            <h2 className="section-title">Equipo de Trabajo</h2>

            <div className="team-topbar">
                <div className="team-topbar-item">
                    <span className="team-topbar-label">Tu código</span>
                    <span className="team-topbar-value">
                        <span className="team-topbar-value-text">{userReferralCode || 'N/A'}</span>
                        {userReferralCode && (
                            <button type="button" className="team-topbar-copy-btn" onClick={() => handleCopy(userReferralCode)} title="Copiar código">
                                <Copy size={14} />
                            </button>
                        )}
                    </span>
                </div>

                <div className="team-topbar-divider" />

                <div className="team-topbar-item">
                    <span className="team-topbar-label">Vinculador</span>
                    <span className="team-topbar-value">{referrerUsername || 'Nadie (Registro Directo)'}</span>
                </div>

                <div className="team-topbar-divider" />

                <div className="team-topbar-item team-topbar-item--grow">
                    <span className="team-topbar-label">Enlace de referido</span>
                    <span className="team-topbar-value team-topbar-link">
                        <span className="team-topbar-value-text">{referralLinkDisplay || 'Completá tu perfil para generarlo'}</span>
                        {referralLink && (
                            <button type="button" className="team-topbar-copy-btn" onClick={() => handleCopy(referralLink)} title="Copiar enlace">
                                <Copy size={14} />
                            </button>
                        )}
                    </span>
                </div>

                <BotonCompartir userProfile={profileData} />
            </div>

            <div className="team-main-row">
                <div className="team-overview-card">
                    <div className="team-overview-main">
                        <span className="team-overview-title">Tu equipo</span>
                        <span className="team-overview-count">{totalMembers}</span>
                        <span className="team-overview-count-label">miembros</span>

                        <div className="team-overview-rank">
                            <span className="team-overview-rank-badge" style={{ backgroundColor: currentRank?.color || '#94a3b8' }}>
                                <Award size={24} />
                            </span>
                            <span className="team-overview-rank-name">{currentRank?.label || 'Sin rango'}</span>
                            <span className="team-overview-rank-points">{(profileData?.points || 0).toLocaleString('es-AR')} pts</span>
                        </div>
                    </div>

                    <div className="team-overview-divider" />

                    <div>
                        <div className="team-overview-figures">
                            <div>
                                <span className="team-overview-figure-label">Volumen total</span>
                                <span className="team-overview-figure-value">${totalVolume.toLocaleString('es-AR')}</span>
                            </div>
                            <div>
                                <span className="team-overview-figure-label">Comisión total</span>
                                <span className="team-overview-figure-value">${totalEarnings.toLocaleString('es-AR')}</span>
                            </div>
                        </div>

                        <span className="team-distribution-title">Distribución por nivel</span>
                        <div className="team-distribution-bar">
                            {totalMembers > 0 ? (
                                levelDistribution.map(({ level, members }) => (
                                    members > 0 && <div key={level} className={`team-distribution-segment level-${level}`} style={{ flexGrow: members }} />
                                ))
                            ) : (
                                <div className="team-distribution-segment level-empty" style={{ flexGrow: 1 }} />
                            )}
                        </div>
                        <div className="team-distribution-labels">
                            {levelDistribution.map(({ level, members }) => (
                                <span key={level} className="team-distribution-label">
                                    <i className={`dot level-${level}`} /> Nivel {level} · {members}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="team-residual-card">
                    <div className="team-residual-header">
                        <div className="team-residual-ring" style={{ '--pct': residualUnlockPercentage }}>
                            <span>{Math.round(residualUnlockPercentage)}%</span>
                        </div>
                        <div className="team-residual-text">
                            <h3>Red profunda (Línea 4 en adelante)</h3>
                            <p>Comisión potencial del 1% sobre lo que ingresa en tu línea 4+. Se desbloquea hasta el monto que tus líneas 1, 2 y 3 combinadas alcancen en este ciclo.</p>
                        </div>
                    </div>

                    <ResidualCommissionDetail commissionCycle={commissionCycle} />
                </div>
            </div>

            <div className="team-nav-cards">
                <button type="button" className="team-nav-card" onClick={() => goTo('red')}>
                    <Network size={22} />
                    <span>Explorar red</span>
                    <ChevronRight size={18} />
                </button>
                <button type="button" className="team-nav-card" onClick={() => goTo('lista')}>
                    <List size={22} />
                    <span>Ver lista</span>
                    <ChevronRight size={18} />
                </button>
                <button type="button" className="team-nav-card" onClick={() => goTo('comisiones')}>
                    <PieChart size={22} />
                    <span>Comisiones</span>
                    <ChevronRight size={18} />
                </button>
            </div>
        </div>
    );
};

export default EquiposDeTrabajo;
